import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import { userAdminApi, usersApi } from '../services/api';

function UserProfile({ user: adminUser, onLogout }) {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [tags, setTags] = useState([]);
  const [stats, setStats] = useState(null);
  const [history, setHistory] = useState([]);
  const [breakdown, setBreakdown] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [audit, setAudit] = useState([]);
  const [logins, setLogins] = useState([]);
  const [tab, setTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [newTag, setNewTag] = useState('');

  useEffect(() => { fetchAll(); }, [userId]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [p, s, h, b, t, a, l] = await Promise.all([
        userAdminApi.getProfile(userId).catch(() => ({})),
        userAdminApi.getStats(userId).catch(() => ({})),
        userAdminApi.getHistory(userId).catch(() => ({})),
        userAdminApi.getVenueBreakdown(userId).catch(() => ({})),
        userAdminApi.getPointsTimeline(userId).catch(() => ({})),
        userAdminApi.getAudit(userId).catch(() => ({})),
        userAdminApi.getLoginHistory(userId).catch(() => ({})),
      ]);
      setProfile(p.user || p.data?.user);
      setTags(p.tags || p.data?.tags || []);
      setStats(s.stats || s.data?.stats);
      setHistory(h.history || h.data?.history || []);
      setBreakdown(b.breakdown || b.data?.breakdown || []);
      setTimeline(t.timeline || t.data?.timeline || []);
      setAudit(a.entries || a.data?.entries || []);
      setLogins(l.history || l.data?.history || []);
      setForm({
        notes: (p.user || p.data?.user)?.notes || '',
        address: (p.user || p.data?.user)?.address || '',
        emergencyContact: (p.user || p.data?.user)?.emergency_contact || '',
        dateOfBirth: (p.user || p.data?.user)?.date_of_birth || '',
      });
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const saveMeta = async () => {
    try {
      await userAdminApi.updateMeta(userId, { ...form, actorId: adminUser?.id });
      setEditing(false);
      fetchAll();
    } catch (e) { alert(e.message); }
  };

  const addTag = async () => {
    if (!newTag.trim()) return;
    const colors = ['#2563eb', '#16a34a', '#dc2626', '#d97706', '#7c3aed', '#0891b2'];
    await userAdminApi.addTag(userId, newTag, colors[Math.floor(Math.random()*colors.length)]);
    setNewTag('');
    fetchAll();
  };

  const exportData = async () => {
    const r = await userAdminApi.exportUser(userId);
    const blob = new Blob([JSON.stringify(r.export || r.data?.export, null, 2)], { type: 'application/json' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `user-${userId}.json`;
    a.click();
  };

  if (loading || !profile) return (
    <div className="admin-layout">
      <Sidebar user={adminUser} onLogout={onLogout} />
      <main className="main-content"><p>Loading...</p></main>
    </div>
  );

  const winRate = stats?.games_played > 0 ? Math.round((stats.wins / stats.games_played) * 100) : 0;
  const initials = `${profile.first_name?.[0] || ''}${profile.last_name?.[0] || ''}`.toUpperCase();
  const hue = (profile.id || '').split('').reduce((a, c) => a + c.charCodeAt(0), 0) % 360;

  return (
    <div className="admin-layout">
      <Sidebar user={adminUser} onLogout={onLogout} />
      <main className="main-content">
        <button className="btn btn-secondary" onClick={() => navigate('/users')}>← Back</button>

        {/* Header card */}
        <div style={{ display: 'flex', gap: 20, padding: 24, background: '#fff', borderRadius: 12, marginTop: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
          <label style={{ cursor: 'pointer' }}>
            {profile.profile_photo_url ? (
              <img src={profile.profile_photo_url} alt="" style={{ width: 96, height: 96, borderRadius: '50%', objectFit: 'cover' }} />
            ) : (
              <div style={{
                width: 96, height: 96, borderRadius: '50%',
                background: `linear-gradient(135deg, hsl(${hue},70%,55%), hsl(${(hue+60)%360},70%,45%))`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: '#fff', fontSize: 36, fontWeight: 800
              }}>{initials || '?'}</div>
            )}
            <input type="file" accept="image/*" style={{ display: 'none' }} onChange={async (e) => {
              const f = e.target.files?.[0]; if (!f) return;
              const reader = new FileReader();
              reader.onload = async () => {
                try { await userAdminApi.uploadPhoto(userId, reader.result); fetchAll(); }
                catch (er) { alert(er.message); }
              };
              reader.readAsDataURL(f);
            }} />
          </label>
          <div style={{ flex: 1 }}>
            <h1 style={{ margin: 0 }}>{profile.first_name} {profile.last_name}</h1>
            <div style={{ color: '#666', marginTop: 4 }}>@{profile.username}</div>
            <div style={{ display: 'flex', gap: 12, marginTop: 8, flexWrap: 'wrap' }}>
              {profile.phone_number && <a href={`tel:${profile.phone_number}`}>📞 {profile.phone_number}</a>}
              {profile.email && <a href={`mailto:${profile.email}`}>✉️ {profile.email}</a>}
              {profile.vip_1 && <span style={{ background: '#fcd34d', color: '#1a2a3e', padding: '2px 10px', borderRadius: 12, fontWeight: 700 }}>⭐ VIP</span>}
              {(profile.roles || []).map(r => <span key={r} style={{ background: '#e0e7ff', color: '#3730a3', padding: '2px 10px', borderRadius: 12 }}>{r}</span>)}
              {tags.map(t => <span key={t.id} style={{ background: t.color || '#6b7280', color: '#fff', padding: '2px 10px', borderRadius: 12 }}>
                {t.tag} <span style={{ cursor: 'pointer' }} onClick={async () => { await userAdminApi.removeTag(userId, t.id); fetchAll(); }}>✕</span>
              </span>)}
            </div>
            <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
              <input value={newTag} onChange={e => setNewTag(e.target.value)} placeholder="Add tag..." style={{ padding: 4, fontSize: 12 }} onKeyDown={e => e.key === 'Enter' && addTag()} />
              <button className="btn btn-secondary btn-sm" onClick={addTag}>+ Tag</button>
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <button className="btn btn-secondary btn-sm" onClick={() => setEditing(!editing)}>{editing ? 'Cancel' : '✏️ Edit'}</button>
            <button className="btn btn-secondary btn-sm" onClick={exportData}>⬇ Export</button>
            <button className="btn btn-danger btn-sm" onClick={async () => {
              if (window.confirm('Reset password to last 4 of phone?')) {
                await userAdminApi.bulkResetPassword([userId], adminUser?.id);
                alert('Password reset');
              }
            }}>🔑 Reset PW</button>
            <button className="btn btn-danger btn-sm" onClick={async () => {
              if (window.confirm('Force logout from all devices?')) {
                await userAdminApi.forceLogout(userId);
                alert('Logged out');
              }
            }}>🚪 Force Logout</button>
          </div>
        </div>

        {/* Stats grid */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, marginTop: 16 }}>
          <Stat label="Games Played" value={stats?.games_played || 0} />
          <Stat label="Wins" value={stats?.wins || 0} />
          <Stat label="Win Rate" value={`${winRate}%`} />
          <Stat label="Podiums" value={stats?.podiums || 0} />
          <Stat label="Best Place" value={stats?.best_place || '—'} />
          <Stat label="Avg Place" value={stats?.avg_place || '—'} />
          <Stat label="Total Points" value={stats?.total_points || 0} />
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: 8, marginTop: 16, borderBottom: '1px solid #e5e7eb' }}>
          {['overview', 'history', 'venues', 'audit', 'logins', 'meta'].map(t => (
            <button key={t} className={`btn ${tab === t ? 'btn-primary' : 'btn-secondary'} btn-sm`} style={{ borderRadius: 0, borderBottom: tab === t ? '2px solid #2563eb' : 'none' }} onClick={() => setTab(t)}>{t}</button>
          ))}
        </div>

        <div style={{ marginTop: 16, background: '#fff', padding: 16, borderRadius: 8 }}>
          {tab === 'overview' && (
            <div>
              <h3>Points over time</h3>
              <Sparkline data={timeline.map(p => parseInt(p.points))} />
              <h3 style={{ marginTop: 16 }}>Top venues</h3>
              {breakdown.slice(0, 5).map(b => (
                <div key={b.name} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #f3f4f6' }}>
                  <span>{b.name}</span><span>{b.count} games · {b.points} pts</span>
                </div>
              ))}
            </div>
          )}
          {tab === 'history' && (
            <table className="data-table">
              <thead><tr><th>Date</th><th>Venue</th><th>Place</th><th>Points</th></tr></thead>
              <tbody>
                {history.map(h => (
                  <tr key={h.id}>
                    <td>{new Date(h.start_time).toLocaleDateString()}</td>
                    <td>{h.venue_name}</td>
                    <td>{h.place}</td>
                    <td>{h.points_awarded}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {tab === 'venues' && breakdown.map(b => (
            <div key={b.name} style={{ padding: 8, borderBottom: '1px solid #f3f4f6' }}>
              {b.name}: {b.count} games · {b.points} pts
            </div>
          ))}
          {tab === 'audit' && audit.map(a => (
            <div key={a.id} style={{ padding: 8, borderBottom: '1px solid #f3f4f6', fontSize: 13 }}>
              <strong>{a.action}</strong> {a.field && `· ${a.field}`} {a.new_value && `→ ${a.new_value}`}
              <div style={{ color: '#888', fontSize: 11 }}>{a.actor_fn || 'system'} · {new Date(a.created_at).toLocaleString()}</div>
            </div>
          ))}
          {tab === 'logins' && (
            logins.length === 0 ? <p>No login history</p> :
            logins.map(l => <div key={l.id} style={{ padding: 6, borderBottom: '1px solid #f3f4f6', fontSize: 12 }}>
              {new Date(l.created_at).toLocaleString()} · {l.ip || '—'} · {l.success ? '✓' : '✗'}
            </div>)
          )}
          {tab === 'meta' && (
            <div>
              {editing ? (
                <>
                  <label>Notes:</label>
                  <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={4} style={{ width: '100%' }} />
                  <label>Address:</label>
                  <input value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} style={{ width: '100%' }} />
                  <label>Emergency contact:</label>
                  <input value={form.emergencyContact} onChange={e => setForm({ ...form, emergencyContact: e.target.value })} style={{ width: '100%' }} />
                  <label>Date of birth:</label>
                  <input type="date" value={form.dateOfBirth?.split('T')[0] || ''} onChange={e => setForm({ ...form, dateOfBirth: e.target.value })} />
                  <div style={{ marginTop: 12 }}>
                    <button className="btn btn-primary" onClick={saveMeta}>Save</button>
                  </div>
                </>
              ) : (
                <>
                  <p><strong>Notes:</strong> {profile.notes || '—'}</p>
                  <p><strong>Address:</strong> {profile.address || '—'}</p>
                  <p><strong>Emergency:</strong> {profile.emergency_contact || '—'}</p>
                  <p><strong>DOB:</strong> {profile.date_of_birth || '—'}</p>
                </>
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div style={{ background: '#fff', padding: 16, borderRadius: 8, boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
      <div style={{ fontSize: 11, color: '#666', textTransform: 'uppercase' }}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 800 }}>{value}</div>
    </div>
  );
}

function Sparkline({ data, width = 600, height = 60 }) {
  if (!data || data.length === 0) return <p>No data</p>;
  const max = Math.max(...data, 1);
  const points = data.map((v, i) => `${(i / Math.max(data.length - 1, 1)) * width},${height - (v / max) * height}`).join(' ');
  return (
    <svg width={width} height={height} style={{ display: 'block' }}>
      <polyline points={points} fill="none" stroke="#2563eb" strokeWidth="2" />
    </svg>
  );
}

export default UserProfile;
